import { memo, useCallback, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import _ from 'lodash';
import { useAnalytics } from 'use-analytics';
import SortingFilter from '../Filters/SortingFilter';
import {
  TripsSortingValues,
  TripsSortingType,
  SortDirection,
  SearchStatus,
  PageLinks,
  UserRolePermissions,
} from '../../constants';
import { searchTripsRequest } from '../../store/slices/trip';
import withSpinner from '../../HOCs/withSpinner';
import TripList from './TripList';
import TripsFilters from './TripList/TripsFilters';
import TripsFreeTextFilter from './TripList/TripsFilters/TripsFreeTextFilter';
import TripsCSVDownloader from './TripsCSVDownloader';
import Button from '../Button';
import checkIsAdmin from '../../helpers/selectors/checkIsAdmin';

const Trips = () => {
  const { track } = useAnalytics();
  const router = useRouter();
  const dispatch = useDispatch();
  const { tripsSearch, searchTripsStatus } = useSelector((store) => store.trip);
  const isViewingHomePage = router.pathname === PageLinks.Home.url;
  const { email, isLoggedIn, travelAgency } = useSelector((store) => store.user);
  const { debug, test, nocode } = router.query;
  const isAdminUser = useSelector(checkIsAdmin);
  const doShowTripsCSVDownloader =
    travelAgency &&
    travelAgency.permissions &&
    travelAgency.permissions.authenticated &&
    (travelAgency.permissions.authenticated.includes(
      UserRolePermissions.canViewOtherAgentsBookedTrips,
    ) ||
      isAdminUser);

  const handleSearch = useCallback(
    (searchObj) => {
      dispatch(searchTripsRequest({ searchObj }));
      track('searchTrips', { ...(searchObj || {}), loggedInUserEmail: email });
    },
    [dispatch, track, email],
  );

  const handleClickViewAll = () => {
    if (isViewingHomePage) {
      const queryString = {};
      if (test === 'true') queryString.test = true;
      if (isLoggedIn && !_.isUndefined(nocode) && nocode !== 'false') queryString.nocode = '';

      router.push({
        pathname: '/trips',
        query: queryString,
      });
    } else {
      const searchObj = {
        ...tripsSearch.searchObj,
        doViewAll: true,
      };
      handleSearch(searchObj);
    }
  };

  const renderTripList = () => (
    <TripList doAllowDeleteBrochure={!isViewingHomePage} doAllowDeleteQuote={!isViewingHomePage} />
  );

  useEffect(() => {
    if (!isViewingHomePage) {
      const selectedUser = {
        value: email,
        label: email,
      };
      handleSearch({
        sortBy: TripsSortingType.RECENT,
        sortDirection: SortDirection.DESCEND,
        selectedUsers: [selectedUser],
        doViewAll: !isViewingHomePage,
        debug: debug === 'true',
      });
    }
    // oxlint-disable-next-line react/exhaustive-deps
  }, []);

  return (
    <div>
      <div className={clsx('flex items-center pb-4', { 'pt-6': isViewingHomePage })}>
        <div className="text-2xl font-bold flex-initial">
          <span className="text-heading font-heading">
            Your {tripsSearch.searchObj.sortBy === TripsSortingType.RECENT ? 'Recent' : ''} Trips
          </span>
        </div>

        {!isViewingHomePage && tripsSearch.filterObj && tripsSearch.filterObj.totalTrips > 0 && (
          <div className="flex-auto ml-2 pt-2">
            <span className="text-gray-600">Showing: </span>
            {tripsSearch.filterObj.totalTrips} trips
          </div>
        )}

        {!isViewingHomePage && doShowTripsCSVDownloader && <TripsCSVDownloader />}
      </div>
      <div className="pb-2 pb-2">
        <div className="flex">
          {!isViewingHomePage && tripsSearch.searchObj && (
            <div className="flex-auto">
              <div>
                <TripsFreeTextFilter onSearch={handleSearch} />
              </div>
            </div>
          )}
          {!isViewingHomePage && tripsSearch.searchObj && (
            <div className="flex-initial place-self-end">
              <div className="ml-2">
                <SortingFilter
                  sortingValues={TripsSortingValues}
                  initialValue={TripsSortingType.RECENT}
                  value={tripsSearch.searchObj.sortBy || TripsSortingType.RECENT}
                  direction={tripsSearch.searchObj.sortDirection || SortDirection.ASCEND}
                  onSortChange={handleSearch}
                  resultsLabel="trips"
                  variant="transparentGrayFilterClasses"
                  widthInside="w-full"
                  isSelectionOptionBlack
                />
              </div>
            </div>
          )}
        </div>
      </div>
      {!isViewingHomePage && <TripsFilters onSearch={handleSearch} />}
      {!isViewingHomePage &&
        withSpinner(
          renderTripList,
          searchTripsStatus === SearchStatus.SEARCHING,
          'Searching trips',
        )}
      {isViewingHomePage && (
        <div className="text-left pb-24 text-theme mx-auto">
          <Button size={Button.buttonSizes.SM} onClick={handleClickViewAll}>
            <span className="font-body">View All Trips</span>
          </Button>
        </div>
      )}
    </div>
  );
};

export default memo(Trips);
